// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/pages/services/tarallo/game2048.ts
// 🔧 PURPOSE: Le regole di "Dal tarallo al trullo" (il 2048), senza React:
//   tavolo 4x4, si scorre in una direzione, due tessere uguali che si
//   toccano diventano una sola del livello dopo (2+2=4 … 1024+1024=2048,
//   il trullo). Dopo ogni mossa valida esce una tessera nuova (2, a volte 4).
//   Le tessere hanno un id: la pagina le anima mentre scorrono; quelle
//   "assorbite" restano un giro come fantasmi, sotto quella nuova.
// ============================================

export type Direction = 'up' | 'down' | 'left' | 'right';

export interface Tile {
  id: number;
  value: number;
  row: number;
  col: number;
  /** Appena uscita (piccola animazione). */
  isNew?: boolean;
  /** Appena nata da una fusione (piccolo salto). */
  merged?: boolean;
  /** Assorbita da una fusione: si vede solo per finire la corsa, poi sparisce. */
  ghost?: boolean;
}

export interface GameState {
  tiles: Tile[];
  score: number;
  moves: number;
  nextId: number;
  /** Fatto il trullo (2048) almeno una volta. */
  won: boolean;
  /** Dopo il trullo l'ospite ha scelto di continuare. */
  keepPlaying: boolean;
  over: boolean;
}

export const SIZE = 4;
export const WIN_VALUE = 2048;

export type Random = () => number;

const live = (s: GameState) => s.tiles.filter((t) => !t.ghost);

function emptyCells(tiles: Tile[]): Array<[number, number]> {
  const taken = new Set(tiles.map((t) => `${t.row},${t.col}`));
  const out: Array<[number, number]> = [];
  for (let r = 0; r < SIZE; r += 1) for (let c = 0; c < SIZE; c += 1) if (!taken.has(`${r},${c}`)) out.push([r, c]);
  return out;
}

function spawn(state: GameState, rnd: Random): GameState {
  const tiles = live(state);
  const cells = emptyCells(tiles);
  if (cells.length === 0) return state;
  const [row, col] = cells[Math.floor(rnd() * cells.length) % cells.length];
  const value = rnd() < 0.9 ? 2 : 4;
  return { ...state, tiles: [...state.tiles, { id: state.nextId, value, row, col, isNew: true }], nextId: state.nextId + 1 };
}

export function newGame(rnd: Random = Math.random): GameState {
  let s: GameState = { tiles: [], score: 0, moves: 0, nextId: 1, won: false, keepPlaying: false, over: false };
  s = spawn(s, rnd);
  s = spawn(s, rnd);
  return s;
}

/** Le quattro file nell'ordine in cui si spingono le tessere (la prima cella è il muro). */
function lines(dir: Direction): Array<Array<[number, number]>> {
  const out: Array<Array<[number, number]>> = [];
  for (let i = 0; i < SIZE; i += 1) {
    const line: Array<[number, number]> = [];
    for (let j = 0; j < SIZE; j += 1) {
      if (dir === 'left') line.push([i, j]);
      else if (dir === 'right') line.push([i, SIZE - 1 - j]);
      else if (dir === 'up') line.push([j, i]);
      else line.push([SIZE - 1 - j, i]);
    }
    out.push(line);
  }
  return out;
}

export function canMove(tiles: Tile[]): boolean {
  const alive = tiles.filter((t) => !t.ghost);
  if (alive.length < SIZE * SIZE) return true;
  const at = new Map(alive.map((t) => [`${t.row},${t.col}`, t.value]));
  for (let r = 0; r < SIZE; r += 1) {
    for (let c = 0; c < SIZE; c += 1) {
      const v = at.get(`${r},${c}`);
      if (v === at.get(`${r},${c + 1}`) || v === at.get(`${r + 1},${c}`)) return true;
    }
  }
  return false;
}

export const maxTile = (s: Pick<GameState, 'tiles'>): number =>
  s.tiles.reduce((m, t) => (t.ghost ? m : Math.max(m, t.value)), 0);

/**
 * Una mossa. Se niente si sposta, torna lo stesso stato (nessuna tessera
 * nuova, la mossa non conta).
 */
export function move(state: GameState, dir: Direction, rnd: Random = Math.random): GameState {
  if (state.over) return state;
  const tiles = live(state).map((t) => ({ ...t, isNew: false, merged: false }));
  const byCell = new Map<string, Tile>(tiles.map((t) => [`${t.row},${t.col}`, t]));
  const next: Tile[] = [];
  let nextId = state.nextId;
  let gained = 0;
  let moved = false;

  for (const line of lines(dir)) {
    const inLine: Tile[] = line.map(([r, c]) => byCell.get(`${r},${c}`)).filter((t): t is Tile => !!t);
    let slot = 0;
    let last: Tile | null = null; // l'ultima tessera posata, se può ancora fondersi
    for (const tile of inLine) {
      if (last && last.value === tile.value) {
        const [r, c] = line[slot - 1];
        // Le due tessere vanno nella cella della fusione e restano come fantasmi.
        next.splice(next.indexOf(last), 1, { ...last, ghost: true });
        next.push({ ...tile, row: r, col: c, ghost: true });
        next.push({ id: nextId, value: tile.value * 2, row: r, col: c, merged: true });
        nextId += 1;
        gained += tile.value * 2;
        moved = true;
        last = null;
      } else {
        const [r, c] = line[slot];
        if (tile.row !== r || tile.col !== c) moved = true;
        const placed: Tile = { ...tile, row: r, col: c };
        next.push(placed);
        last = placed;
        slot += 1;
      }
    }
  }

  if (!moved) return state;
  let s: GameState = { ...state, tiles: next, score: state.score + gained, moves: state.moves + 1, nextId };
  s = spawn(s, rnd);
  const top = maxTile(s);
  s = { ...s, won: s.won || top >= WIN_VALUE, over: !canMove(s.tiles) };
  return s;
}

// ---- Salvataggio della partita (si riprende riaprendo la pagina) -----------------

const SAVE_KEY = 'nva.tarallo2048.game.v1';
const BEST_KEY = 'nva.tarallo2048.best.v1';

export function saveGame(s: GameState): void {
  try {
    const clean = { ...s, tiles: live(s).map(({ id, value, row, col }) => ({ id, value, row, col })) };
    window.localStorage.setItem(SAVE_KEY, JSON.stringify(clean));
  } catch {
    // salvataggio bloccato: la partita vale per questa visita
  }
}

export function loadGame(): GameState | null {
  try {
    const raw = JSON.parse(window.localStorage.getItem(SAVE_KEY) || 'null');
    if (!raw || !Array.isArray(raw.tiles)) return null;
    const tiles: Tile[] = raw.tiles.filter(
      (t: Tile) =>
        Number.isInteger(t?.id) && Number.isInteger(t?.value) && t.value >= 2 &&
        Number.isInteger(t?.row) && Number.isInteger(t?.col) && t.row >= 0 && t.row < SIZE && t.col >= 0 && t.col < SIZE,
    );
    if (tiles.length === 0) return null;
    return {
      tiles,
      score: Number(raw.score) || 0,
      moves: Number(raw.moves) || 0,
      nextId: Math.max(Number(raw.nextId) || 0, ...tiles.map((t) => t.id + 1)),
      won: !!raw.won,
      keepPlaying: !!raw.keepPlaying,
      over: !canMove(tiles),
    };
  } catch {
    return null;
  }
}

export function clearSavedGame(): void {
  try {
    window.localStorage.removeItem(SAVE_KEY);
  } catch {
    // niente
  }
}

export function loadBest(): number {
  try {
    return Number(window.localStorage.getItem(BEST_KEY)) || 0;
  } catch {
    return 0;
  }
}

export function saveBest(score: number): void {
  try {
    if (score > loadBest()) window.localStorage.setItem(BEST_KEY, String(score));
  } catch {
    // niente
  }
}
